import React, { useEffect, useState } from "react";
import {
    Heading,
    NotFoundMessage,
    UnauthorisedMessage,
} from "@peracto/peracto-ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowCircleLeft } from "@fortawesome/pro-regular-svg-icons/faArrowCircleLeft";

import { Link, useParams, Redirect } from "react-router-dom";
import {
    GET_ONE,
    UPDATE,
    useClient,
    getSchemaFromResource,
} from "@peracto/client";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import sharedYupFields from "../sharedYupFields";
import { useAuth } from "@peracto/peracto-auth";
import { canEditIbtConfig, formatDataForApi, formatDataForForm, getIbtConfigViolations, setViolationFieldErrors, showLocationSaveErrorToasts } from "../util";

const LocationsEditContainer = ({ children }) => {
    return (
        <div className="form-container">
            <Heading name="Edit Location">
                <div className="flex-grow-1 d-flex align-items-center justify-content-end">
                    <Link className="btn btn-outline-primary" to="/locations">
                        <FontAwesomeIcon
                            icon={faArrowCircleLeft}
                            className="mr-2"
                        />
                        Back to Locations
                    </Link>
                </div>
            </Heading>
            {children}
        </div>
    );
};

export const LocationsEdit = ({ LocationsForm }) => {
    const { client, getResource } = useClient();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [unauthorised, setUnauthorised] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [formData, setFormData] = useState(false);
    const [redirect, setRedirect] = useState("");
    const { locationId } = useParams();

    const schema = getSchemaFromResource(getResource("locations")).shape(
        sharedYupFields
    );

    const onSubmit = async (data, actions) => {
        actions.setStatus(undefined);

        try {
            await client(UPDATE, "locations", {
                id: `locations/${locationId}`,
                data: formatDataForApi(data, {
                    canEditIbt: canEditIbtConfig(user),
                    initialIbtConfig: formData.ibtConfig,
                }),
            });

            actions.setSubmitting(false);

            fetchLocation();
            toast.success("Location successfully updated!");
        } catch (e) {
            const violations = e?.error?.body?.violations || [];
            let ibtConfigViolations = [];

            if (violations.length > 0) {
                // Display errors for invalid fields.
                actions.setSubmitting(false);
                setViolationFieldErrors(actions.setFieldError, violations);

                // Kept separately (not just via setFieldError) so the IBT
                // section's error summary can be built from the raw
                // propertyPath/message pairs without depending on Formik's
                // errors shape, which can't hold both an indexed
                // (ibtConfig[0].branchSequence[0]) and whole-set (ibtConfig)
                // violation on the same key at once.
                ibtConfigViolations = getIbtConfigViolations(violations);
                if (ibtConfigViolations.length > 0) {
                    actions.setStatus({ ibtConfigViolations });
                }
            }
            console.error(e);
            showLocationSaveErrorToasts(e, violations);
            actions.setSubmitting(false);
        }
    };

    const fetchLocation = async () => {
        try {
            const { data: locationData, response: locationResponse } =
                await client(GET_ONE, "locations", {
                    id: `locations/${locationId}`,
                });

            const formattedData = formatDataForForm(locationData);

            setFormData(formattedData);

            setLoading(false);

            if (locationResponse.status === 404) {
                setRedirect("/locations");
            }
        } catch (e) {
            if (e.status === 403) {
                setUnauthorised(true);
            }

            if (e.status === 404) {
                setNotFound(true);
            }

            setLoading(false);
            setRedirect("/locations");
        }
    };

    useEffect(() => {
        fetchLocation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (unauthorised) {
        return <UnauthorisedMessage />;
    }

    if (notFound) {
        return (
            <NotFoundMessage
                url="/locations"
                message="The location you're looking for could not be found"
                buttonLabel="Go to Locations"
            />
        );
    }

    return loading ? (
        <LocationsEditContainer>
            <div className="card">
                <div className="card-body">Loading...</div>
            </div>
        </LocationsEditContainer>
    ) : (
        <>
            {redirect && <Redirect to={redirect} />}

            <Helmet>
                <title>{formData.name || "Location"} | Edit | Peracto</title>
            </Helmet>

            <div className="form-container">
                <Heading name="Edit Location">
                    <div className="flex-grow-1 d-flex align-items-center justify-content-end">
                        <Link
                            className="btn btn-outline-primary"
                            to="/locations"
                        >
                            <FontAwesomeIcon
                                icon={faArrowCircleLeft}
                                className="mr-2"
                            />
                            Back to Locations
                        </Link>
                    </div>
                </Heading>
                <LocationsForm
                    modeEdit
                    data={formData}
                    onSubmit={onSubmit}
                    schema={schema}
                />
            </div>
        </>
    );
};
